import produce from 'immer';
import React, {useCallback, useEffect, useMemo} from 'react';

import {FormDefinitionContent, FormDefinitionField, FormSectionType} from 'forms/form-designer/Constants';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';

import {
  FieldKeyToTableColumnIndex,
  FormInstanceComputedTableState,
  FormInstanceTableState,
} from './Constants';
import FormInstanceSection from './FormInstanceSection';
import {useAsyncComputedTables} from './FormInstanceTableState';

interface FormInstanceContentsProps {
  formDefinitionContent: FormDefinitionContent;
  formInstanceTableState: FormInstanceTableState;
  onUpdateFormInstanceTableState: (state: FormInstanceTableState) => void;
  onUpdateFormInstanceComputedTableState?: (state: FormInstanceComputedTableState) => void;
  fieldKeyToTableColumnIndex: FieldKeyToTableColumnIndex;
}

/** The innards of a Form Instance. Like a giant controlled input, it handles the actual editing of the Form's
 * state, but requires the state to be stored by a parent.
 */
export function FormInstanceContents(props: FormInstanceContentsProps): JSX.Element | null {
  const {
    formDefinitionContent,
    formInstanceTableState,
    onUpdateFormInstanceTableState,
    onUpdateFormInstanceComputedTableState,
    fieldKeyToTableColumnIndex,
  } = props;
  const computedTableState = useAsyncComputedTables(formInstanceTableState);

  useEffect(() => {
    if (onUpdateFormInstanceComputedTableState) {
      onUpdateFormInstanceComputedTableState(computedTableState);
    }
  }, [computedTableState, onUpdateFormInstanceComputedTableState]);

  const sharedFields: Array<FormDefinitionField> = useMemo(
    () =>
      formDefinitionContent.sections
        .filter((section) => section.sectionType === FormSectionType.STATIC)
        .flatMap((section) => section.content)
        // ignore nested sections
        .filter(
          (sectionOrField): sectionOrField is FormDefinitionField => sectionOrField.type === 'FormField'
        ),
    [formDefinitionContent.sections]
  );

  /** adds a row to all specified tables, including any additional values from shared fields */
  const addFormTableRow = useCallback(
    (tableKeys: Array<string>): void => {
      onUpdateFormInstanceTableState(
        produce(formInstanceTableState, (draft) => {
          // for each table associated with the added repeatable section
          for (const tableKey of tableKeys) {
            const table = formInstanceTableState[tableKey];
            const adapter = getStructuredTableAdapterFromTable(table);
            // create a new blank row
            const newRow = adapter.getBlankRow(table.metadata, table.rows.length + 1, table.columns);
            // for each shared field in the table, fill in the values from the first row
            const sharedRow = table.rows[0];
            const sharedFieldsInTable = sharedFields.filter(
              (field) => fieldKeyToTableColumnIndex[field.key][0] === tableKey
            );
            for (const field of sharedFieldsInTable) {
              const [, columnIndex] = fieldKeyToTableColumnIndex[field.key];
              const sharedCell = sharedRow?.cells?.[columnIndex];
              if (sharedCell) {
                newRow.cells[columnIndex].typedValue = sharedCell.typedValue;
                newRow.cells[columnIndex].text = sharedCell.text;
              }
            }
            draft[tableKey].rows.push(newRow);
          }
        })
      );
    },
    [fieldKeyToTableColumnIndex, formInstanceTableState, onUpdateFormInstanceTableState, sharedFields]
  );

  /** removes a specified row from all provided tables */
  const removeFormTableRow = useCallback(
    (tableKeys: Array<string>, rowIndex: number): void => {
      onUpdateFormInstanceTableState(
        produce(formInstanceTableState, (draft) => {
          for (const tableKey of tableKeys) {
            draft[tableKey].rows.splice(rowIndex, 1);
          }
        })
      );
    },
    [formInstanceTableState, onUpdateFormInstanceTableState]
  );

  return (
    <>
      {formDefinitionContent.sections.map((section, index) => (
        <FormInstanceSection
          key={section.key}
          definition={section}
          autoFocusFirstField={index === 0}
          tableState={formInstanceTableState}
          computedTableState={computedTableState}
          updateTableState={onUpdateFormInstanceTableState}
          fieldKeyToTableColumnIndex={fieldKeyToTableColumnIndex}
          onAddRepeatableSection={addFormTableRow}
          onRemoveRepeatableSection={removeFormTableRow}
          itemName="Task"
        />
      ))}
    </>
  );
}
